import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../../utils/authFetch';

export const fetchFindings = createAsyncThunk(
  'agencyFindings/fetchAll',
  async (args = {}, { rejectWithValue }) => {
    const {
      assessmentId, mandateId, ruleId, agentId,
      severity, status, finding_type, parent_finding_id, include_deleted,
    } = args;
    try {
      // Built up conditionally, not seeded with `{ assessmentId }`: that spelling sent the literal
      // string "assessmentId=undefined" whenever the caller had no assessment, which is every
      // caller since findings moved off assessments.
      const params = new URLSearchParams();
      if (assessmentId) params.set('assessmentId', assessmentId);
      if (mandateId) params.set('mandateId', mandateId);
      if (ruleId) params.set('ruleId', ruleId);
      if (agentId) params.set('agentId', agentId);
      if (severity) params.set('severity', severity);
      if (status) params.set('status', status);
      if (finding_type) params.set('finding_type', finding_type);
      if (parent_finding_id) params.set('parent_finding_id', parent_finding_id);
      if (include_deleted) params.set('include_deleted', 'true');
      const response = await authFetch(`/.netlify/functions/findings-list?${params}`, { method: 'GET' });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const createFinding = createAsyncThunk(
  'agencyFindings/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-create', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateFinding = createAsyncThunk(
  'agencyFindings/update',
  async (data, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-update', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteFinding = createAsyncThunk(
  'agencyFindings/delete',
  async ({ findingId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-delete', {
        method: 'POST',
        body: JSON.stringify({ findingId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      return { findingId, finding: data.finding };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const restoreFinding = createAsyncThunk(
  'agencyFindings/restore',
  async ({ findingId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-restore', {
        method: 'POST',
        body: JSON.stringify({ findingId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const reorderFindings = createAsyncThunk(
  'agencyFindings/reorder',
  async ({ assessmentId, findingType, orderedIds }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-reorder', {
        method: 'POST',
        body: JSON.stringify({ assessmentId, findingType, orderedIds }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return { findingType, orderedIds };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const discardFinding = createAsyncThunk(
  'agencyFindings/discard',
  async ({ findingId, reason }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-discard', {
        method: 'POST',
        body: JSON.stringify({ findingId, reason }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const refineFindingLLM = createAsyncThunk(
  'agencyFindings/refineLLM',
  async ({ findingId, field, instruction }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-refine-llm', {
        method: 'POST',
        body: JSON.stringify({ findingId, field, instruction }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const suggestFindingName = createAsyncThunk(
  'agencyFindings/suggestName',
  async ({ findingId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-suggest-name', {
        method: 'POST',
        body: JSON.stringify({ findingId }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const reviewFinding = createAsyncThunk(
  'agencyFindings/review',
  async ({ findingId, status, comment }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/findings-review', {
        method: 'POST',
        body: JSON.stringify({ findingId, status, comment }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return rejectWithValue(err.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const findingsSlice = createSlice({
  name: 'agencyFindings',
  initialState: {
    items: [],
    // Distinct from `items.length === 0`, and the sidebar badge is why. An empty list before the
    // first fetch and an empty list after one mean opposite things — "we do not know yet" and
    // "there are none" — and a badge that shows 0 for the first is asserting a fact it has not
    // established. Everything in this product is shown with its basis; a count is no exception.
    loaded: false,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearFindingsError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFindings.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchFindings.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.items = action.payload.findings || [];
      })
      .addCase(fetchFindings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch findings';
      })
      .addCase(createFinding.fulfilled, (state, action) => {
        const created = action.payload.finding;
        if (created) state.items.push(created);
      })
      .addCase(updateFinding.pending, (state) => { state.saving = true; })
      .addCase(updateFinding.fulfilled, (state, action) => {
        state.saving = false;
        const updated = action.payload.finding;
        if (updated) {
          const idx = state.items.findIndex(f => f.id === updated.id);
          // Merged, not replaced. findings-list embeds the agent, mandate and rule a finding hangs
          // off; the write handlers return the bare row from `.select().single()`. Assigning that
          // wholesale dropped the relations, so recording a decision blanked the Agent and Against
          // columns until the next full refetch.
          if (idx >= 0) state.items[idx] = { ...state.items[idx], ...updated };
        }
      })
      .addCase(updateFinding.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload || 'Failed to update finding';
      })
      .addCase(deleteFinding.fulfilled, (state, action) => {
        // Soft delete — keep the row but mark deleted_at so include_deleted refetches still work
        const id = action.payload.findingId;
        const idx = state.items.findIndex(f => f.id === id);
        if (idx >= 0) state.items[idx] = action.payload.finding || { ...state.items[idx], deleted_at: new Date().toISOString() };
      })
      .addCase(restoreFinding.fulfilled, (state, action) => {
        const restored = action.payload.finding;
        if (restored) {
          const idx = state.items.findIndex(f => f.id === restored.id);
          if (idx >= 0) state.items[idx] = restored;
        }
      })
      .addCase(reorderFindings.fulfilled, (state, action) => {
        const { findingType, orderedIds } = action.payload;
        // Apply local sort_order so the UI updates without a refetch
        const orderMap = new Map(orderedIds.map((id, i) => [id, i]));
        state.items = state.items.map((f) => (
          f.finding_type === findingType && orderMap.has(f.id)
            ? { ...f, sort_order: orderMap.get(f.id) }
            : f
        ));
      })
      // Same merge, same reason as updateFinding above.
      .addCase(discardFinding.fulfilled, (state, action) => {
        const updated = action.payload.finding;
        if (updated) {
          const idx = state.items.findIndex(f => f.id === updated.id);
          if (idx >= 0) state.items[idx] = { ...state.items[idx], ...updated };
        }
      })
      .addCase(reviewFinding.fulfilled, (state /* , action */) => {
        // Review row is created — the finding's status is mirrored server-side.
        // Caller refetches if it cares about updated finding row.
        state.saving = false;
      });
  },
});

export const { clearFindingsError } = findingsSlice.actions;
export const selectFindings = (state) => state.findings?.items || [];
export const selectFindingsLoading = (state) => state.findings?.loading || false;
export const selectFindingsSaving = (state) => state.findings?.saving || false;
export const selectFindingsError = (state) => state.findings?.error || null;
export default findingsSlice.reducer;
